import { TFunction } from 'i18next';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import {
	Box,
	Button,
	ClappingHandsOutline,
	CloseButton,
	HashtagOutline,
	Heading,
	HeartOutline,
	HStack,
	LightningBoltOutline,
	Modal,
	ModalBody,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	rem,
	Text,
	TrophyOutline,
	VStack,
} from '@bonusly/design-system';

interface CreateCelebrationSpotlightModalProps {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: () => void;
	isLoading?: boolean;
}

/** Auto-sizing pill with translated text */
const TagPill: FC<{ children: React.ReactNode; rotation: number; zIndex: number }> = ({ children, rotation, zIndex }) => (
	<Box
		bg="teal.100"
		_dark={{ bg: 'teal.200' }}
		color="gray.900"
		px={3}
		py={1.5}
		borderRadius="full"
		fontSize="11px"
		fontWeight="semibold"
		whiteSpace="nowrap"
		transform={`rotate(${rotation}deg)`}
		boxShadow="sm"
		lineHeight={1.2}
		position="relative"
		zIndex={zIndex}
	>
		{children}
	</Box>
);

/**
 * Spotlight tags illustration with translatable text and decorative icons.
 * Pills automatically resize based on translated text length.
 * Icons are positioned relative to the pills so they don't overlap with longer translations.
 */
const SpotlightTagsIllustration: FC<{ t: TFunction }> = ({ t }) => (
	<HStack spacing={0} align="center" position="relative" h="100px">
		{/* Left side icons */}
		<VStack spacing={4} align="flex-end" w="30px" mr={1}>
			<Box color="teal.300" transform="rotate(-12deg)">
				<ClappingHandsOutline boxSize="26px" />
			</Box>
			<Box color="teal.300" transform="rotate(-8deg)">
				<LightningBoltOutline boxSize="22px" />
			</Box>
		</VStack>

		{/* Tag pills - stacked with slight rotations, auto-sizing to text */}
		<VStack spacing={0.5} align="center" py={2}>
			<TagPill rotation={-15} zIndex={3}>
				{t('createCelebrationSpotlightModal.banner.tags.topSupporters')}
			</TagPill>
			<TagPill rotation={-3} zIndex={2}>
				{t('createCelebrationSpotlightModal.banner.tags.superpowers')}
			</TagPill>
			<TagPill rotation={7} zIndex={1}>
				{t('createCelebrationSpotlightModal.banner.tags.topValues')}
			</TagPill>
		</VStack>

		{/* Right side icons */}
		<VStack spacing={2} align="flex-start" w="30px" ml={1}>
			<Box color="teal.300" transform="rotate(10deg)">
				<TrophyOutline boxSize="24px" />
			</Box>
			<Box color="teal.300" transform="rotate(8deg)">
				<HeartOutline boxSize="22px" />
			</Box>
			<Box color="teal.300" transform="rotate(15deg)">
				<HashtagOutline boxSize="22px" />
			</Box>
		</VStack>
	</HStack>
);

/**
 * Gradient decoration that appears at the bottom of the banner section.
 * Uses three color bands (yellow-green, teal, purple) with blur effect.
 * Edges fade gracefully to match the background.
 */
const BannerGradient: FC = () => (
	<Box position="absolute" bottom={0} left={6} right={6} zIndex={0} pointerEvents="none" display="flex" alignItems="flex-end" filter="blur(20px)">
		{/* Yellow-green section - fades in from left edge */}
		<Box
			flex={1}
			h="15px"
			bgGradient="linear(to-tr, transparent 0%, #E0F860 40%, transparent 100%)"
			_dark={{ bgGradient: 'linear(to-tr, transparent 0%, #3D5828 40%, transparent 100%)' }}
			transform="rotate(10deg)"
			transformOrigin="bottom center"
		/>
		{/* Teal section */}
		<Box
			flex={2}
			h="20px"
			bgGradient="linear(to-t, #4DDCBE, transparent)"
			_dark={{ bgGradient: 'linear(to-t, #2A6868, transparent)' }}
			transform="rotate(-5deg)"
			transformOrigin="bottom center"
		/>
		{/* Purple section - fades out to right edge, shifted down and stretched left */}
		<Box
			flex={2}
			h="25px"
			mb="-30px"
			ml="-30px"
			bgGradient="linear(to-tl, transparent 0%, #8A78F8 40%, transparent 100%)"
			_dark={{ bgGradient: 'linear(to-tl, transparent 0%, #3D3D7C 40%, transparent 100%)' }}
			transform="rotate(2deg)"
			transformOrigin="bottom center"
		/>
	</Box>
);

/**
 * Modal for creating a spotlight from a celebration bonus (e.g., work anniversary).
 * Shows translatable tags illustration with gradient decoration, "How it works" section, and action buttons.
 */
export const CreateCelebrationSpotlightModal: FC<CreateCelebrationSpotlightModalProps> = ({ isOpen, onClose, onConfirm, isLoading = false }) => {
	const { t } = useTranslation('spotlight');

	return (
		<Modal isCentered isOpen={isOpen} onClose={onClose} size="xl">
			<ModalOverlay />
			<ModalContent borderRadius={rem(24)}>
				<ModalHeader borderBottom="none" color="text.default" pt={rem(20)}>
					{t('createCelebrationSpotlightModal.title')}
				</ModalHeader>
				<Box position="absolute" top={4} right={4}>
					<CloseButton borderRadius="full" bg="secondary.bgAlpha" _hover={{ bg: 'secondary.bgAlphaHover' }} onClick={onClose} w={rem(32)} h={rem(32)} />
				</Box>

				<ModalBody p={0}>
					{/* Banner section with text, illustration, and gradient */}
					<Box position="relative" overflow="hidden" px={6} py={3}>
						{/* Content: side-by-side on desktop, wraps and centers on mobile */}
						<Box display="flex" flexWrap="wrap" alignItems="center" gap={4} position="relative" zIndex={1}>
							{/* Text content */}
							<Box flex="1 1 250px" minW="200px">
								<VStack align="flex-start" spacing={2}>
									<Heading as="h3" size="md" color="text.default" textAlign="left">
										{t('createCelebrationSpotlightModal.banner.heading')}
									</Heading>
									<Text color="text.soft" fontSize="sm" textAlign="left" maxW={rem(240)}>
										{t('createCelebrationSpotlightModal.banner.description')}
									</Text>
								</VStack>
							</Box>

							{/* Tags illustration - centered when wrapped to its own row */}
							<Box flex="1 1 180px" display="flex" justifyContent="center">
								<SpotlightTagsIllustration t={t} />
							</Box>
						</Box>

						{/* Gradient at the bottom */}
						<BannerGradient />
					</Box>

					{/* How it works section */}
					<Box px={6} py={5}>
						<VStack align="flex-start" spacing={2}>
							<Text fontWeight="semibold" color="text.default" fontSize="sm">
								{t('createCelebrationSpotlightModal.howItWorks.label')}
							</Text>
							<Text color="text.soft" fontSize="sm">
								{t('createCelebrationSpotlightModal.howItWorks.description')}
							</Text>
						</VStack>
					</Box>
				</ModalBody>

				<ModalFooter pt={0} pb={rem(24)}>
					<HStack spacing={3} width="100%" justify="flex-end">
						<Button isLoading={isLoading} onClick={onConfirm}>
							{t('createCelebrationSpotlightModal.buttons.generate')}
						</Button>
					</HStack>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};
